import { Author, AuthorNotificationPreferences } from '../api/Author';
import {
    buildAuthorUpdatePayload,
    requireAuthorPayload,
    shouldFallbackToLegacyAuthorTokenLookup,
} from '../domains/authors';
import { SupabaseService, supabase } from './SupabaseClient';
import { serviceMappers } from './serviceMappers';
import { PUBLIC_AUTHOR_FIELDS } from './serviceSelectors';

export class AuthorService extends SupabaseService {
    private normalizeAuthor(author: Author): Author {
        return serviceMappers.normalizeAuthorAvatar(author);
    }

    private normalizeAuthors(authors: Author[]): Author[] {
        return authors.map((author) => this.normalizeAuthor(author));
    }

    private async fetchPublicAuthorById(id: number): Promise<{
        data: Author | null;
        error: { message?: string } | null;
    }> {
        const { data, error } = await supabase
            .from('authors')
            .select(PUBLIC_AUTHOR_FIELDS)
            .eq('id', id)
            .single();

        return {
            data: (data as Author | null) || null,
            error,
        };
    }

    private async fetchAllPublicAuthors(): Promise<{
        data: Author[];
        error: { message?: string } | null;
    }> {
        const { data, error } = await supabase
            .from('authors')
            .select(PUBLIC_AUTHOR_FIELDS)
            .order('name', { ascending: true });

        return {
            data: (data as Author[] | null) || [],
            error,
        };
    }

    private async getAuthorByLegacyToken(token: string): Promise<Author | null> {
        const { data, error } = await supabase
            .from('authors')
            .select(PUBLIC_AUTHOR_FIELDS)
            .eq('token', token)
            .is('token_revoked_at', null)
            .single();

        if (error || !data) {
            return null;
        }

        return this.normalizeAuthor(data);
    }

    private async getAuthorByTokenRpc(token: string): Promise<{
        data: Author | null;
        error: { message?: string } | null;
    }> {
        const { data, error } = await supabase
            .rpc('author_by_token', { client_token: token })
            .maybeSingle();

        return {
            data: (data as Author | null) || null,
            error,
        };
    }

    private async callNotificationPreferencesRpc<T>(
        fn: 'get_current_author_notifications' | 'update_current_author_notifications',
        args?: Record<string, unknown>
    ): Promise<T | null> {
        const supabaseClient = await this.getClient();
        const query = args
            ? supabaseClient.rpc(fn, args)
            : supabaseClient.rpc(fn);
        const { data, error } = await query.maybeSingle();

        if (error) throw new Error(error.message);
        return (data as T | null) || null;
    }

    public async validateTokenAndGetAuthor(token: string): Promise<Author | null> {
        // Prefer the RPC from migration phase 1. Fallback keeps compatibility before SQL rollout.
        const { data: rpcData, error: rpcError } = await this.getAuthorByTokenRpc(token);

        if (!rpcError) {
            return rpcData ? this.normalizeAuthor(rpcData) : null;
        }

        if (!shouldFallbackToLegacyAuthorTokenLookup(rpcError)) {
            return null;
        }

        return this.getAuthorByLegacyToken(token);
    }

    public async getAuthorById(id: number): Promise<Author | null> {
        const { data, error } = await this.fetchPublicAuthorById(id);
        if (error) {
            return null;
        }
        return data ? this.normalizeAuthor(data) : null;
    }

    public async getAllAuthors(): Promise<Author[]> {
        const { data, error } = await this.fetchAllPublicAuthors();

        if (error) {
            console.error('Error fetching all authors:', error);
            throw new Error(error.message);
        }
        return this.normalizeAuthors(data);
    }

    /**
     * Met à jour les informations d'un auteur.
     * @param authorData Les données partielles de l'auteur à mettre à jour.
     */
    public async updateAuthor(authorData: Partial<Author>): Promise<Author> {
        const supabaseClient = await this.getClient();
        const { data, error } = await supabaseClient
            .from('authors')
            .update(buildAuthorUpdatePayload(authorData))
            .eq('id', authorData.id!)
            .select(PUBLIC_AUTHOR_FIELDS)
            .single();

        if (error) throw new Error(error.message);
        return this.normalizeAuthor(
            requireAuthorPayload(data, 'Author update returned no data.')
        );
    }

    public async getMyNotificationPreferences(): Promise<AuthorNotificationPreferences | null> {
        return this.callNotificationPreferencesRpc<AuthorNotificationPreferences>(
            'get_current_author_notifications'
        );
    }

    public async updateMyNotificationPreferences(
        preferences: AuthorNotificationPreferences
    ): Promise<AuthorNotificationPreferences> {
        const data = await this.callNotificationPreferencesRpc<AuthorNotificationPreferences>(
            'update_current_author_notifications',
            {
                client_email: preferences.email,
                client_notify_email: preferences.notify_email_enabled,
            }
        );
        if (!data) throw new Error('No notification preferences returned by server.');
        return data;
    }
}

export const authorService = new AuthorService();
